import { useState, useEffect } from 'react'
import RealtimeControls from './RealtimeControls'
import ProfessionalList from './ProfessionalList'

export default function RealtimeMode({ 
  messages, 
  messagesEndRef, 
  isListening, 
  transcript,
  userTranscript,
  aiTranscript,
  isConnecting,
  onToggleListening, 
  onStartVideo, 
  onEndSession,
  onImageUpload,
  isInspectionMode,
  videoRef,
  isVideoMode,
  onCaptureFrame
}) {
  const [showBorder, setShowBorder] = useState(false)

  // Scroll detection for header border
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop
      setShowBorder(scrollTop > 0)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Dynamic status states
  const getStatusText = () => {
    if (isListening) return "Listening"
    return "Listening" // Default state
  }

  const getConnectionStatus = () => {
    // This could be "active", "stable", "frozen", etc.
    return "active" // For now, always active
  }

  const [fullscreenGallery, setFullscreenGallery] = useState(null)
  const [touchStart, setTouchStart] = useState(0)
  const [touchEnd, setTouchEnd] = useState(0)

  // Swipe handlers
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX)
  }

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const handleTouchEnd = () => {
    if (!fullscreenGallery) return
    
    const swipeDistance = touchStart - touchEnd
    const minSwipeDistance = 50

    if (swipeDistance > minSwipeDistance) {
      goToNext()
    } else if (swipeDistance < -minSwipeDistance) {
      goToPrevious()
    }
  }

  const goToNext = () => {
    if (!fullscreenGallery) return
    const nextIndex = (fullscreenGallery.currentIndex + 1) % fullscreenGallery.images.length
    setFullscreenGallery({ ...fullscreenGallery, currentIndex: nextIndex })
  }

  const goToPrevious = () => {
    if (!fullscreenGallery) return
    const prevIndex = (fullscreenGallery.currentIndex - 1 + fullscreenGallery.images.length) % fullscreenGallery.images.length
    setFullscreenGallery({ ...fullscreenGallery, currentIndex: prevIndex })
  }

  const openGallery = (images, index) => {
    setFullscreenGallery({ images, currentIndex: index })
  }

  const closeGallery = () => {
    setFullscreenGallery(null)
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header - ChatGPT Voice style - Sticky */}
      <div className={`sticky top-0 z-30 bg-white flex items-center p-6 pb-4 transition-all duration-200 ${showBorder ? 'border-b border-gray-200 shadow-sm' : ''}`}>
        <h1 className="text-xl font-semibold text-gray-900">
          AI Inspect <span className="text-gray-500 font-normal">{isInspectionMode ? 'Live Inspection' : 'Voice'}</span>
        </h1>
      </div>

      {/* Video Feed - Show when in inspection mode */}
      {isInspectionMode && isVideoMode && (
        <div className="relative bg-black">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-64 object-cover"
          />
          
          {/* Capture Frame Button - Floating over video */}
          <button
            onClick={() => onCaptureFrame(messages => messages)}
            className="absolute bottom-4 right-4 w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-lg hover:bg-gray-100 transition-colors"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <circle cx="12" cy="12" r="3" fill="currentColor"/>
            </svg>
          </button>

          {/* Instructions Overlay */}
          <div className="absolute top-4 left-4 right-4 bg-black bg-opacity-70 text-white text-sm rounded-lg p-3">
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
              </svg>
              <div>
                <div className="font-medium">Live AI Analysis Active</div>
                <div className="text-xs mt-1 opacity-90">Point camera at issue • Speak naturally • Tap 📸 to capture</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Messages in realtime mode - Chat style (User right, AI left) */}
      <div className="flex-1 overflow-y-auto px-4 py-8">
        <div className="max-w-3xl mx-auto space-y-4">
          {messages.map(msg => {
            const isUser = msg.sender === 'user'
            const isAI = msg.sender === 'ai'
            const isSystem = msg.sender === 'system'
            
            if (isSystem) {
              return (
                <div key={msg.id} className="text-center text-sm text-gray-500 py-2">
                  {msg.text}
                </div>
              )
            }

            // Image thumbnails for user messages
            if (msg.type === 'images' && msg.images) {
              return (
                <div key={msg.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[75%]`}>
                    <div className="flex flex-wrap gap-2">
                      {msg.images.map((img, idx) => (
                        <div key={idx} className="relative group">
                          <img
                            src={img.dataUrl}
                            alt={img.name}
                            onClick={() => openGallery(msg.images, idx)}
                            className="w-24 h-24 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity border-2 border-blue-300"
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded-lg transition-all flex items-center justify-center">
                            <svg className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M15 3h6v6h-2V5h-4V3zM9 3H3v6h2V5h4V3zm6 18h6v-6h-2v4h-4v2zM9 21H3v-6h2v4h4v2z"/>
                            </svg>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{msg.text}</div>
                  </div>
                </div>
              )
            }

            // Professional matching results
            if ((msg.type === 'professionals' || msg.type === 'professional_cards') && msg.professionals) {
              return (
                <div key={msg.id} className="w-full px-4">
                  <ProfessionalList
                    professionals={msg.professionals}
                    recommendation={msg.recommendation}
                    criteria={msg.criteria}
                    searchQuery={msg.searchQuery}
                    noResults={msg.noResults}
                  />
                </div>
              )
            }
            
            return (
              <div key={msg.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${
                  isUser 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-100 text-gray-900'
                }`}>
                  <div className="text-base leading-relaxed whitespace-pre-wrap">
                    {msg.text}
                  </div>
                </div>
              </div>
            )
          })}
          
          {/* Show live user transcript as interim message (User bubble - right side) */}
          {userTranscript && userTranscript.trim() && isListening && (
            <div className="flex justify-end">
              <div className="max-w-[75%] rounded-2xl px-4 py-2 bg-blue-400 text-white opacity-70">
                <div className="text-base leading-relaxed whitespace-pre-wrap italic">
                  {userTranscript}
                </div>
              </div>
            </div>
          )}
          
          {/* Show live AI transcript as interim message (AI bubble - left side) */}
          {aiTranscript && aiTranscript.trim() && (
            <div className="flex justify-start">
              <div className="max-w-[75%] rounded-2xl px-4 py-2 bg-gray-200 text-gray-900 opacity-70">
                <div className="text-base leading-relaxed whitespace-pre-wrap italic">
                  {aiTranscript}
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Bottom controls - Using RealtimeControls component */}
      <div className="sticky bottom-0 z-20 p-4 bg-white">
        <RealtimeControls
          isListening={isListening}
          transcript={transcript}
          isConnecting={isConnecting}
          onToggleListening={onToggleListening}
          onStartVideo={onStartVideo}
          onEndSession={onEndSession}
          onImageUpload={onImageUpload}
        />
        
        <div className="text-xs text-gray-500 text-center mt-2">
          AI Inspect can make mistakes. Check important info.
        </div>
      </div>

      {/* Fullscreen Image Carousel */}
      {fullscreenGallery && (
        <div 
          className="fixed inset-0 bg-black z-50 flex items-center justify-center"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Close Button */}
          <button
            onClick={closeGallery}
            className="absolute top-6 right-6 z-10 text-white hover:text-gray-300 transition-colors"
          >
            <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Image Counter */}
          <div className="absolute top-6 left-1/2 transform -translate-x-1/2 z-10 text-white text-sm bg-black bg-opacity-50 px-4 py-2 rounded-full">
            {fullscreenGallery.currentIndex + 1} / {fullscreenGallery.images.length}
          </div>

          {/* Previous Button */}
          {fullscreenGallery.images.length > 1 && (
            <button
              onClick={goToPrevious}
              className="absolute left-4 z-10 text-white hover:text-gray-300 transition-colors bg-black bg-opacity-50 rounded-full p-3"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          {/* Current Image */}
          <img
            src={fullscreenGallery.images[fullscreenGallery.currentIndex].dataUrl}
            alt={fullscreenGallery.images[fullscreenGallery.currentIndex].name}
            className="w-full h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Next Button */}
          {fullscreenGallery.images.length > 1 && (
            <button
              onClick={goToNext}
              className="absolute right-4 z-10 text-white hover:text-gray-300 transition-colors bg-black bg-opacity-50 rounded-full p-3"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}

          {/* Thumbnail Strip */}
          {fullscreenGallery.images.length > 1 && (
            <div className="absolute bottom-6 left-0 right-0 z-10 flex justify-center gap-2 px-4 overflow-x-auto">
              {fullscreenGallery.images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setFullscreenGallery({ ...fullscreenGallery, currentIndex: idx })}
                  className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                    idx === fullscreenGallery.currentIndex 
                      ? 'border-white scale-110' 
                      : 'border-gray-500 opacity-60'
                  }`}
                >
                  <img
                    src={img.dataUrl}
                    alt={img.name}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
